import React from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Switch,
  Button,
  Chip,
  Divider,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { useThemeMode } from '../theme/ThemeContext';

export default function SettingsPage() {
  const theme = useTheme();
  const { isDark, toggleTheme, restoreTheme, setThemeMode } = useThemeMode();
  const currentUser = useSelector((state) => state.auth?.user);

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: alpha('#0B3B60', 0.1),
              color: 'primary.main',
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
              System & Workspace Settings
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Customize your interface theme, visual preferences, and cooperative workspace.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Appearance & Theme Section */}
        <Grid item xs={12} md={8}>
          <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: 3.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <PaletteRoundedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Interface Theme
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Choose how MTC-FS looks to you. Select a clean corporate light theme or an executive dark theme.
              </Typography>

              {/* Theme Preview Selector Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Light Mode Card */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    onClick={() => setThemeMode('light')}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: !isDark ? 'primary.main' : 'divider',
                      bgcolor: !isDark ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LightModeRoundedIcon sx={{ color: '#F59E0B' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Light Theme
                        </Typography>
                      </Stack>
                      {!isDark && <CheckCircleRoundedIcon color="primary" sx={{ fontSize: 20 }} />}
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      Clean corporate styling featuring Mutual Trust Navy and emerald accents with crisp daylight readability.
                    </Typography>
                  </Paper>
                </Grid>

                {/* Dark Mode Card */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    onClick={() => setThemeMode('dark')}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: isDark ? 'primary.main' : 'divider',
                      bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <DarkModeRoundedIcon sx={{ color: '#38BDF8' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Dark Theme
                        </Typography>
                      </Stack>
                      {isDark && <CheckCircleRoundedIcon color="primary" sx={{ fontSize: 20 }} />}
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      Executive deep slate mode designed for low-light environments and extended audit sessions.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Fast Toggle Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Quick Dark Mode Toggle
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Switch instantly between dark and light appearance
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LightModeRoundedIcon sx={{ fontSize: 18, color: !isDark ? '#F59E0B' : 'text.disabled' }} />
                  <Switch checked={isDark} onChange={toggleTheme} color="primary" />
                  <DarkModeRoundedIcon sx={{ fontSize: 18, color: isDark ? '#38BDF8' : 'text.disabled' }} />
                </Stack>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              {/* Restore Default Theme Button */}
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Restore Default Theme
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Reset all custom appearance adjustments back to original system defaults
                  </Typography>
                </Box>
                <Button
                  onClick={restoreTheme}
                  variant="outlined"
                  color="inherit"
                  startIcon={<RestartAltRoundedIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Restore Theme
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Account & Platform Info Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <SecurityRoundedIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Account Context
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Signed in as
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {currentUser?.firstName
                      ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim()
                      : currentUser?.name || currentUser?.username || 'Member'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Username
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                    @{currentUser?.username || '—'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Assigned Role
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={sessionStorage.getItem('mtc_view_as_role') ? `Simulating: ${sessionStorage.getItem('mtc_view_as_role')}` : (currentUser?.roles?.[0]?.name || currentUser?.role || 'Member')}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Theme preference is automatically remembered on this device.
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
